/*
 * Token counting utilities using js-tiktoken.
 * Provides functions to count tokens in messages and conversations for context window management.
 */

import { Tiktoken } from "js-tiktoken/lite";

let encoder = null;
let encodingPromise = null;

/**
 * Context window limits for different models (in tokens).
 */
export const CONTEXT_LIMITS = {
  "qwen3.7-plus": 131072,
  "qwen3.6-plus": 131072,
  "qwen3.5-plus": 131072,
  "qwen3-max-2026-01-23": 32768,
  "qwen3-coder-next": 131072,
  "qwen3-coder-plus": 131072,
  "glm-4.7": 32768,
  "glm-5": 131072,
  "kimi-k2.5": 131072,
  "MiniMax-M2.5": 131072,
  "mimo-v2.5-pro": 131072,
  "mimo-v2.5": 131072,
};

/**
 * Get context window limit for a model.
 * @param {string} model - Model name
 * @returns {number} Context window limit in tokens
 */
export function getContextLimit(model) {
  return CONTEXT_LIMITS[model] || 32768; // Conservative default
}

/**
 * Lazily initialize the tokenizer encoder.
 * Uses cl100k_base encoding which is compatible with most OpenAI-like models.
 * @returns {Promise<Tiktoken>} Encoder instance
 */
async function getEncoder() {
  if (encoder) return encoder;

  if (!encodingPromise) {
    encodingPromise = (async () => {
      try {
        // Try to load from CDN first (smaller bundle)
        const response = await fetch("https://tiktoken.pages.dev/js/cl100k_base.json");
        if (response.ok) {
          const data = await response.json();
          encoder = new Tiktoken(data, /<\|([0-9]+)\|>/g, 100257);
          return encoder;
        }
      } catch (e) {
        // CDN failed, try bundled version
        console.warn("Failed to load tokenizer from CDN, using bundled version:", e);
      }

      try {
        // Fallback to bundled version
        const data = await import("js-tiktoken/ranks/cl100k_base");
        encoder = new Tiktoken(data.default, /<\|([0-9]+)\|>/g, 100257);
        return encoder;
      } catch (e) {
        console.error("Failed to initialize tokenizer:", e);
        throw new Error("Could not initialize token counter");
      }
    })();
  }

  return encodingPromise;
}

/**
 * Count tokens in a string.
 * @param {string} text - Text to count tokens for
 * @returns {Promise<number>} Number of tokens
 */
export async function countTokens(text) {
  if (!text) return 0;
  try {
    const enc = await getEncoder();
    return enc.encode(text).length;
  } catch (e) {
    console.error("Error counting tokens:", e);
    // Fallback: estimate ~4 characters per token
    return Math.ceil(text.length / 4);
  }
}

/**
 * Count tokens in a single message (OpenAI format).
 * Includes overhead for message structure.
 * @param {Object} message - Message object with role, content, etc.
 * @returns {Promise<number>} Number of tokens
 */
export async function countMessageTokens(message) {
  const enc = await getEncoder();
  let tokens = 3; // Per OpenAI: each message has ~3 tokens overhead

  for (const [key, value] of Object.entries(message)) {
    if (typeof value === "string") {
      tokens += enc.encode(value).length;
      if (key === "name") tokens += 1;
    } else if (Array.isArray(value)) {
      // Handle tool_calls, etc.
      tokens += enc.encode(JSON.stringify(value)).length;
    }
  }

  return tokens;
}

/**
 * Count tokens in a conversation (array of messages).
 * @param {Array} messages - Array of message objects
 * @returns {Promise<number>} Total number of tokens
 */
export async function countConversationTokens(messages) {
  let total = 3; // 3 tokens for assistant reply primer

  for (const msg of messages) {
    total += await countMessageTokens(msg);
  }

  return total;
}

/**
 * Truncate conversation history to fit within token budget.
 * Uses sliding window approach: keeps most recent messages, removes oldest.
 * System prompt is always preserved.
 *
 * @param {Array} history - Conversation history (without system prompt)
 * @param {number} maxTokens - Maximum tokens to keep
 * @returns {Promise<Array>} Truncated history
 */
export async function truncateHistory(history, maxTokens) {
  if (history.length === 0) return history;

  const currentTokens = await countConversationTokens(history);
  if (currentTokens <= maxTokens) return history;

  // Work backwards from end, keeping messages until budget is exhausted
  const kept = [];
  let tokensUsed = 0;

  for (let i = history.length - 1; i >= 0; i--) {
    const msgTokens = await countMessageTokens(history[i]);
    if (tokensUsed + msgTokens > maxTokens) break;
    kept.unshift(history[i]);
    tokensUsed += msgTokens;
  }

  return kept;
}

/**
 * Truncate tool results in history to reduce token usage.
 * Replaces old tool results with summaries.
 *
 * @param {Array} history - Conversation history
 * @param {number} keepRecent - Number of recent messages to keep intact
 * @returns {Array} History with truncated tool results
 */
export function truncateToolResults(history, keepRecent = 10) {
  if (history.length <= keepRecent) return history;

  return history.map((msg, idx) => {
    // Keep recent messages intact
    if (idx >= history.length - keepRecent) return msg;

    // Truncate old tool results
    if (msg.role === "tool" && msg.content) {
      try {
        const result = JSON.parse(msg.content);
        if (result.csv) {
          // Replace CSV with summary
          return {
            ...msg,
            content: JSON.stringify({
              ok: result.ok,
              summary: `[Tool result: ${result.rows || "?"} rows × ${result.columns || "?"} columns]`,
            }),
          };
        }
      } catch (e) {
        // Not JSON or parse error, leave as is
      }
    }

    return msg;
  });
}

/**
 * Get token usage percentage for a conversation.
 * @param {Array} messages - Conversation messages
 * @param {string} model - Model name
 * @returns {Promise<Object>} Usage info with percentage
 */
export async function getTokenUsage(messages, model) {
  const tokens = await countConversationTokens(messages);
  const limit = getContextLimit(model);
  const percentage = (tokens / limit) * 100;

  return {
    tokens,
    limit,
    percentage,
    status: percentage > 95 ? "critical" : percentage > 80 ? "warning" : "normal",
  };
}
